import * as React from 'react';
import { EQualificationAnswersType, IQualificationDialogProps } from './types';
import './_QualificationDialog.scss';
import {
    NOT_SKIPPABLE_QUESTION_KEYS,
    QUALIFICATION_BACK,
    QUALIFICATION_FIRST_QUESTION,
    QUALIFICATION_HIDE_TIMEOUT,
    QUALIFICATION_QUESTIONS_TITLE,
    QUALIFICATION_SKIP,
    QUALIFICATION_SKIPPED_NO,
    QUALIFICATION_SKIPPED_NO_OTHER,
    QUALIFICATION_SKIPPED_SUBTITLE,
    QUALIFICATION_SKIPPED_SUBTITLE_OTHER,
    QUALIFICATION_SKIPPED_TITLE,
    QUALIFICATION_SKIPPED_YES_OTHER,
} from '../../constants';
import { ModalPage } from '../../../../base/components/ModalPage';
import { getMessages } from '../../../../../../store/constants';
import { Button, LoaderBlock } from 'kui';
import { getNextQuestionKey } from '../../helpers/getNextQuestionKey';
import { QUALIFICATION_QUESTIONS } from '../../constants/questions';
import { QualificationDialogAnswersRadio } from '../QualificationDialogAnswersRadio/QualificationDialogAnswersRadio';
import { IUserMetaQualificationAnswer } from '../../../../../../types/rest/IUserMeta';
import { QualificationDialogAnswersText } from '../QualificationDialogAnswersText/QualificationDialogAnswersText';
import {
    QualificationDialogAnswersCheckbox
} from '../QualificationDialogAnswersCheckbox/QualificationDialogAnswersCheckbox';
import { UserpicStorage } from 'app/view/react_components/base/components/UserpicStorage/UserpicStorage';
import { AfterSkipDialog } from '../../../../base/components/AfterSkipDialog/AfterSkipDialog';
import { AFTER_SKIP_DIALOG_YES } from '../../../../base/components/AfterSkipDialog/constants';

export function QualificationDialog ({
    answers,
    fullName,
    initials,
    permissionId,
    photoUrl,
    firstQuestionKey,
    onDidMount,
    onAnswer,
    onCompleted,
    onSkipped,
    onGetStarted,
    onShowCalendar
}: IQualificationDialogProps) {
    const className = 'qualification-dialog';
    const classBody = className + '__body';
    const classBodyItem = classBody + '-item';
    const classSection = className + '__section';
    const classSectionHeader = classSection + '-header';
    const classSectionBody = classSection + '-body';

    const [questionKey, setQuestionKey] = React.useState(firstQuestionKey || QUALIFICATION_FIRST_QUESTION);
    const [prevQuestionKey, setPrevQuestionKey] = React.useState(null); // Только для эффекта перехода к следующему вопросу, рисуется поверх текущего и скрывается постепенно
    const [skippedQuestionKey, setSkippedQuestionKey] = React.useState(null);
    const [skipped, setSkipped] = React.useState(false);
    const [isShowSkipped, setShowSkipped] = React.useState(null);
    const [completed, setCompleted] = React.useState(false);
    const [isLoading, setLoading] = React.useState(false);
    const [otherSkipAnswer, setOtherSkipAnswer] = React.useState(null);

    const goToQuestion = (
        key: string
    ) => {
        setPrevQuestionKey(questionKey);
        setQuestionKey(key);
        setTimeout(() => {
            setPrevQuestionKey(null);
        }, QUALIFICATION_HIDE_TIMEOUT)
    }

    const onSkipHandler = () => {
        setSkippedQuestionKey(questionKey);
        setSkipped(true);
        setTimeout(() => {
            setQuestionKey(null);
        }, QUALIFICATION_HIDE_TIMEOUT);
    }

    const onCancelSkip = () => {
        setQuestionKey(skippedQuestionKey);
        setSkippedQuestionKey(null);
        setSkipped(false);
        setShowSkipped(false);
    };

    const questionsScreen = !completed && !skipped;

    const getBackQuestionKey = (): string => {
        let result = null;
        Object.keys(QUALIFICATION_QUESTIONS).forEach(qKey => {
            const question = QUALIFICATION_QUESTIONS[qKey];
            const possibleNextQuestions = [];
            const _answers = answers && Object.values(answers).map(item => item && item.key);
            if (question.nextQuestion) possibleNextQuestions.push(question.nextQuestion);
            Object.values(question.answers).forEach(answer => {
                if (answer.nextQuestion) possibleNextQuestions.push(answer.nextQuestion(_answers));
            })
            if (possibleNextQuestions.includes(questionKey)) {
                result = qKey;
            }
        })
        return result;
    }
    const backKey = questionsScreen && getBackQuestionKey();

    const onBack = () => {
        if (backKey) {
            onAnswer(backKey, null);
            goToQuestion(backKey);
        }
    }

    const answerHandler = (
        answer: IUserMetaQualificationAnswer,
        isSkip?: boolean
    ) => {
        if (isSkip) {
            setOtherSkipAnswer(answer);
        } else {
            setLoading(true);
            onAnswer(questionKey, answer)
                .then(() => {
                    const nextQuestion = getNextQuestionKey(questionKey, answer.key, answers && Object.values(answers).map(item => item && item.key));
                    if (nextQuestion) {
                        setTimeout(() => {
                            goToQuestion(nextQuestion);
                            setLoading(false);
                        }, 500);
                    } else {
                        setLoading(false);
                        setTimeout(() => {
                            onCompleted();
                        }, 1000);
                        setCompleted(true);
                        setTimeout(() => {
                            setQuestionKey(null);
                        }, QUALIFICATION_HIDE_TIMEOUT)
                    }
                });
        }
    }
    const onFreeTextSave = (text: string) => {
        const question = QUALIFICATION_QUESTIONS[questionKey];
        const answerKeys = question.answers ? Object.keys(question.answers) : [];
        if (answerKeys.length) {
            const answer: IUserMetaQualificationAnswer = {
                key: answerKeys[0],
                text
            }
            answerHandler(answer);
        }
    }

    React.useEffect(() => {
        if (!skipped) return;
        let isNotSkippable = false;
        for (let key in answers) {
            if (
                NOT_SKIPPABLE_QUESTION_KEYS.includes(key) ||
                NOT_SKIPPABLE_QUESTION_KEYS.includes(skippedQuestionKey)
            ) {
                onSkipped();
                onGetStarted();
                isNotSkippable = true;
                break;
            }
        }
        if (!isNotSkippable) {
            setShowSkipped(true);
        }
    }, [answers, skippedQuestionKey, skipped]);

    React.useEffect(() => {
        if (questionsScreen || isShowSkipped) return;
        onGetStarted();
    }, [isShowSkipped, completed]);

    return (
        <ModalPage
            title={getMessages().getText('qualification.welcome', null, fullName)}
            onDidMount={onDidMount}
            className={className}
            showBackButton={false}
            rightContent={
                <UserpicStorage
                    initials={initials}
                    src={photoUrl}
                    size={40}
                    userId={permissionId}
                />
            }
        >
            <div className={classBody}>
                {isLoading && <LoaderBlock/>}
                {(!questionsScreen && isShowSkipped || otherSkipAnswer) &&
                    <AfterSkipDialog
                        yesLabel={otherSkipAnswer ? QUALIFICATION_SKIPPED_YES_OTHER : AFTER_SKIP_DIALOG_YES}
                        noLabel={otherSkipAnswer ? QUALIFICATION_SKIPPED_NO_OTHER : QUALIFICATION_SKIPPED_NO}
                        title={otherSkipAnswer ? null : QUALIFICATION_SKIPPED_TITLE}
                        subtitle={otherSkipAnswer ? QUALIFICATION_SKIPPED_SUBTITLE_OTHER : QUALIFICATION_SKIPPED_SUBTITLE}
                        onApply={() => {
                            if (otherSkipAnswer) {
                                onAnswer(questionKey, { ...otherSkipAnswer, key: 'personal_development' }).then(() => {
                                    setTimeout(() => {
                                        onCompleted();
                                    }, 1000);
                                    setCompleted(true);
                                    setTimeout(() => {
                                        setQuestionKey(null);
                                    }, QUALIFICATION_HIDE_TIMEOUT);
                                });
                            } else {
                                onSkipped();4
                                onGetStarted();
                            }
                        }}
                        onCancel={() => {
                            if (otherSkipAnswer) {
                                onAnswer(questionKey, { ...otherSkipAnswer, key: 'blank' }).then(() => {
                                    setTimeout(() => {
                                        onCompleted();
                                    }, 1000);
                                    setCompleted(true);
                                    setTimeout(() => {
                                        setQuestionKey(null);
                                    }, QUALIFICATION_HIDE_TIMEOUT);
                                });
                            } else {
                                onCancelSkip();
                            }
                        }}
                    />
                }
                {/*{!questionsScreen && !isShowSkipped &&*/}
                {/*    <div className={classBodyItem}>*/}
                {/*        <div*/}
                {/*            className={className + '__title'}>{QUALIFICATION_COMPLETED_TITLE}</div>*/}
                {/*        <div className={classSubtitle}>*/}
                {/*            {ReactHtmlParser(QUALIFICATION_COMPLETED_SUBTITLE)}*/}
                {/*        </div>*/}
                {/*        <Button*/}
                {/*            size={'large'}*/}
                {/*            className={className + '__get-started'}*/}
                {/*            onClick={onGetStarted}*/}
                {/*        >*/}
                {/*            {QUALIFICATION_GET_STARTED}*/}
                {/*        </Button>*/}
                {/*    </div>*/}
                {/*}*/}
                {!otherSkipAnswer &&
                    <div className={`
                        ${classBodyItem}
                        ${!questionsScreen ? classBodyItem + '--prev-screen' : ''}
                    `}>
                        <div className={className + '__title'}>{QUALIFICATION_QUESTIONS_TITLE}</div>
                        {/*<div className={`*/}
                        {/*    ${classSubtitle}*/}
                        {/*    ${classSubtitle + '--double-trial'}*/}
                        {/*`}*/}
                        {/*>*/}
                        {/*    {QUALIFICATION_QUESTIONS_SUBTITLE}*/}
                        {/*</div>*/}
                        <div className={className + '__questions'}>
                            {
                                [questionKey, prevQuestionKey].map((questionKeyToRender, index) => {
                                    if (!questionKeyToRender) return null;
                                    const question = QUALIFICATION_QUESTIONS[questionKeyToRender];
                                    return (
                                        <div
                                            className={`
                                            ${classSection}
                                            ${index === 1 ? classSection + '--prev-screen' : ''}
                                        `}
                                            key={questionKeyToRender + index}
                                        >
                                            <div className={classSectionHeader}>
                                                {question.question}
                                            </div>
                                            <div className={classSectionBody}>
                                                {question.answersType === EQualificationAnswersType.FREE_TEXT &&
                                                    <QualificationDialogAnswersText
                                                        questionKey={questionKeyToRender}
                                                        showSaveButton={true}
                                                        onSave={onFreeTextSave}
                                                    />
                                                }
                                                {question.answersType === EQualificationAnswersType.CHECKBOXES &&
                                                    <QualificationDialogAnswersCheckbox
                                                        answers={answers && Object.values(answers).map(item => item && item.key)}
                                                        questionKey={questionKeyToRender}
                                                        onAnswer={answerHandler}
                                                        onShowCalendar={onShowCalendar}
                                                    />
                                                }
                                                {(!question.answersType || question.answersType === EQualificationAnswersType.RADIO_SELECT) &&
                                                    <QualificationDialogAnswersRadio
                                                        answers={answers && Object.values(answers).map(item => item && item.key)}
                                                        key={questionKeyToRender + index}
                                                        questionKey={questionKeyToRender}
                                                        onAnswer={answerHandler}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    )
                                })
                            }
                        </div>
                        <div className={className + '__footer'}>
                            <Button
                                variant={'icon-text'}
                                text={QUALIFICATION_SKIP}
                                onClick={onSkipHandler}
                            />
                            {backKey &&
                                <Button
                                    variant={'icon-text'}
                                    text={QUALIFICATION_BACK}
                                    onClick={onBack}
                                />
                            }
                        </div>
                    </div>
                }
            </div>
        </ModalPage>
    );
}
